import { useEffect, useMemo, useState } from 'react';
import { Box, Table, Td, Tr } from '@chakra-ui/react';
import { Colors, Text } from '@mercator-gcp/grout';
import { ProjectDetail } from '@mercator-gcp/types/backend/Project';
import StreamingText from 'components/elements/StreamingText';
import { titleCase } from 'utils/misc';

const STYLES = {
  table: { 'th, td': { border: 'none' } },
  header: {
    padding: 'padding_8',
    alignItems: 'top',
    verticalAlign: 'top',
    color: Colors.TextQuiet,
  },
  body: {
    padding: 'padding_8',
    textAlign: 'left',
    width: '100%',
  },
};

type DisplayAIResponseProps = {
  existingAiInsights?: ProjectDetail['ai_insights'];
  forceStreaming?: boolean; // Force streaming animation even if user has seen it before
  projectSlug?: string; // Project slug to make localStorage key unique per project
};

/**
 * Deduplicates repeated tags within a statement and ensures tags are moved to the end of the statement.
 */
const cleanAndReorderTags = (text: string): string => {
  const tagPattern = /\[[A-Z]+\]/g;

  return text
    .split('\n')
    .filter((line) => line.trim() !== '') // Remove empty lines
    .map((line) => {
      const tags = line.match(tagPattern) || [];
      const cleanStatement = line.replace(tagPattern, '');
      if (tags.length === 0) return line; // No tags, return original line

      // Use only the first tag and move it to the end of the statement
      return `${cleanStatement} ${tags[0]}\n`;
    })
    .join('\n');
};

const SectionRow = ({
  title,
  content,
  isStreaming,
  onComplete,
}: {
  title: string;
  content: string;
  isStreaming: boolean;
  onComplete: () => void;
}) => (
  <Tr>
    <Td sx={STYLES.header}>
      <Text.SmallLabel>{title}</Text.SmallLabel>
    </Td>
    <Td sx={STYLES.body}>
      <StreamingText
        text={content}
        speed={isStreaming ? 20 : 0}
        onComplete={isStreaming ? onComplete : undefined}
      />
    </Td>
  </Tr>
);

const STAKEHOLDER_KEY = 'stakeholders';
const NEXT_STEPS_KEY = 'next_steps';
const OTHER_SECTIONS_KEY = 'other_sections';
const ANALYSIS_KEY = 'analysis';

const HIDDEN_KEYS = ['project_name', 'scope', 'predicted_project_value'];

const getStorageKey = (slug: string) => `hasSeenAIInsightsStreaming_${slug}`;

const DisplayAIResponse = ({
  existingAiInsights,
  forceStreaming = false,
  projectSlug = '',
}: DisplayAIResponseProps) => {
  const [activeIndex, setActiveIndex] = useState(0);

  // Initialize stream state: check localStorage once
  const shouldStream = useMemo(() => {
    if (forceStreaming || !projectSlug) return true;

    return !localStorage.getItem(getStorageKey(projectSlug));
  }, []);

  useEffect(() => {
    if (shouldStream && projectSlug && !forceStreaming) {
      localStorage.setItem(getStorageKey(projectSlug), 'true');
    }
    // If not streaming, jump to the end immediately
    if (!shouldStream) setActiveIndex(99);
  }, [shouldStream, projectSlug, forceStreaming]);

  const sections = useMemo(() => {
    if (!existingAiInsights) return [];

    // Extract values into variables to help TypeScript narrow the types
    const stakeholders = existingAiInsights[STAKEHOLDER_KEY];
    const nextSteps = existingAiInsights[NEXT_STEPS_KEY];

    // Prepare Analysis (Catch-all)
    const analysisContent = Object.entries(existingAiInsights)
      .filter(
        ([key, value]) =>
          ![STAKEHOLDER_KEY, NEXT_STEPS_KEY, ...HIDDEN_KEYS].includes(key) && value,
      )
      .reduce((acc, [key, value]) => {
        const formattedKey = titleCase(key.replace(/_/g, ' '));
        if (key !== ANALYSIS_KEY && key !== OTHER_SECTIONS_KEY)
          acc.push(`## ${formattedKey}`);

        // flatten other sections
        if (key === OTHER_SECTIONS_KEY && Array.isArray(value)) {
          value.forEach((section) => {
            acc.push(`## ${titleCase(section.header)}`);
            acc.push(cleanAndReorderTags(section.content));
          });

          return acc;
        }

        if (Array.isArray(value)) {
          acc.push(...value.map(String).map(cleanAndReorderTags));
        } else {
          acc.push(cleanAndReorderTags(String(value)));
        }

        return acc;
      }, [] as string[])
      .join('\n\n');

    // Build the configuration array
    const config = [
      {
        id: ANALYSIS_KEY,
        title: ANALYSIS_KEY,
        content: analysisContent,
      },
      {
        id: STAKEHOLDER_KEY,
        title: STAKEHOLDER_KEY,
        content: Array.isArray(stakeholders)
          ? stakeholders
              .map((s) => cleanAndReorderTags(s).split(/;\s+/g).join('<br>'))
              .join('\n\n')
          : '',
      },
      {
        id: NEXT_STEPS_KEY,
        title: 'next steps',
        // Narrowing 'nextSteps' to an array via the check
        content: Array.isArray(nextSteps)
          ? nextSteps.map((s) => `- ${s.trim()}`).join('\n')
          : '',
      },
    ];

    return config.filter((s) => s.content.length > 0);
  }, [existingAiInsights]);

  if (!existingAiInsights || sections.length === 0) return null;

  return (
    <>
      <Box>
        <Table sx={STYLES.table}>
          {sections.map((section, index) => {
            // Visibility logic: Show if we aren't streaming, or if we've reached this index
            const isVisible = !shouldStream || index <= activeIndex;
            if (!isVisible) return null;

            return (
              <SectionRow
                key={section.id}
                title={section.title}
                content={section.content}
                isStreaming={shouldStream && index === activeIndex}
                onComplete={() => setActiveIndex((prev) => prev + 1)}
              />
            );
          })}
        </Table>
      </Box>
      <Text.Small color={Colors.TextQuiet}>
        This content was generated by AI and should be independently verified for
        accuracy.
      </Text.Small>
    </>
  );
};

export default DisplayAIResponse;
